import { ConfirmIcon } from '@blocksuite/affine-components/icons';
import { toast } from '@blocksuite/affine-components/toast';
import type { AttachmentBlockModel } from '@blocksuite/affine-model';
import {
  CitationProvider,
  edgelessToolbarI18nT,
} from '@blocksuite/affine-shared/services';
import {
  composeBlobNameFromRename,
  resolveBlobDisplayName,
  splitBlobNameForRename,
} from '@blocksuite/affine-shared/utils';
import type { EditorHost } from '@blocksuite/std';
import { html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';

import { renameStyles } from './styles';

export const RenameModal = ({
  editorHost,
  model,
  abortController,
}: {
  editorHost: EditorHost;
  model: AttachmentBlockModel;
  abortController: AbortController;
}) => {
  const inputRef = createRef<HTMLInputElement>();
  // Fix auto focus
  setTimeout(() => inputRef.value?.focus());
  const { editableName, extension } = splitBlobNameForRename(
    resolveBlobDisplayName({
      blobId: model.props.sourceId,
      occurrenceName: model.props.name,
      mimeType: model.props.type,
    })
  );

  let fileName = editableName;
  const citationService = editorHost.std.get(CitationProvider);

  const abort = () => abortController.abort();
  const onConfirm = () => {
    const newFileName = composeBlobNameFromRename(fileName, extension);
    if (!newFileName) {
      toast(editorHost, edgelessToolbarI18nT('toast.fileNameEmpty'));
      return;
    }
    model.store.updateBlock(model, {
      name: newFileName,
    });
    if (citationService.isCitationModel(model)) {
      citationService.trackEvent('Edit');
    }
    abort();
  };
  const onInput = (e: InputEvent) => {
    fileName = (e.target as HTMLInputElement).value;
  };
  const onKeydown = (e: KeyboardEvent) => {
    e.stopPropagation();

    if (e.key === 'Escape' && !e.isComposing) {
      abort();
      return;
    }
    if (e.key === 'Enter' && !e.isComposing) {
      onConfirm();
      return;
    }
  };

  return html`
    <style>
      ${renameStyles}
    </style>
    <div class="affine-attachment-rename-overlay-mask" @click="${abort}"></div>
    <div class="affine-attachment-rename-container">
      <div class="affine-attachment-rename-input-wrapper">
        <input
          ${ref(inputRef)}
          type="text"
          .value=${fileName}
          @input=${onInput}
          @keydown=${onKeydown}
        />
        <span class="affine-attachment-rename-extension">${extension}</span>
      </div>
      <editor-icon-button
        class="affine-confirm-button"
        .iconSize=${'24px'}
        @click=${onConfirm}
      >
        ${ConfirmIcon}
      </editor-icon-button>
    </div>
  `;
};
